import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  TrackByFunction,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AccurateDateIntervalWithAbsence,
  AccurateDateIntervalWithShift,
  AccurateDateIntervalWithStay,
  GroupedUserDataV2,
  INNER_ROW_PADDING_BOTTOM_PX,
  INNER_ROW_PADDING_TOP_PX,
  LocationAndDateTimeRange,
  RoleAction,
  RolePermissionSubject,
  ROW_PADDING,
  StaffLayoutStyle,
  StayCardPosition,
  StaySuggestionInfo,
  UserTimelines,
} from '@wilson/interfaces';
import {
  DeferredClusterCardHelperService,
  GanttChartTimeDifferenceComponent,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  MessageWithUndoOptionComponent,
  SelectedItemOutput,
  ShiftCardTooltipStateService,
  ShiftMultiSelectHelperService,
  ShiftValidationTooltipWrapperComponent,
  TimeDifferenceWidthPxPipe,
  TimeLineAbsenceTooltipComponent,
  ToDateTimeRangeV2Pipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ShiftTimelinePipesModule,
  ToAbsenceDataPipe,
} from '@wilson/shift-timeline/pipes';
import {
  ChangedKeyManagerService,
  CreateNewViaDragAndDropService,
  DetermineShiftRenderDatetimePipe,
  DragAndDropModeService,
  getDateFromPixelPosition,
  PreferredSettingsStateFacadeService,
} from '@wilson/shift-timeline/services';
import {
  AddActivityToUnassignedRegion,
  SetDemarcatorRange,
  AssignedRegionVisibilityStateService,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertActivityV2,
  ShiftTimelineDataState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  firstValueFrom,
  Observable,
  pairwise,
  Subscription,
} from 'rxjs';
import { ActivityDropZoneHelperService } from '../../components/activity-drop-zone/activity-drop-zone-helper.service';
import { ActivityGapFillAnimationComponent } from '../../components/activity-gap-fill-animation/activity-gap-fill-animation.component';
import { ActivityResetDropZoneComponent } from '../../components/activity-reset-zone/activity-reset-drop-zone.component';
import { ShiftTransferHandlerV2Service } from '../../components/add-transfer-modal/shift-transfer-handler-v2.service';
import { ActivityDrawerCloseActionType } from '../../components/drawers/TimelineActivityDrawer/timeline-activity-drawer.service';
import { NewAbsenceDropZoneComponent } from '../../components/new-absence-drop-zone/new-absence-drop-zone.component';
import { TimelineUserAbsenceComponent } from '../../components/timeline-user-absence/timeline-user-absence.component';
import { AbsenceCardPosition } from '../../interfaces/shift-timeline-custom-types';
import { MouseMovementHelperService } from '../../services/mouse-movement-helper.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { UpdateShiftOnActivityDragService } from '../../services/update-shift-on-activity-drag.service';
import { ActivityDropZoneV2Component } from '../activity-drop-zone-v2/activity-drop-zone-v2.component';
import { ActivityMoveDropZoneV2Component } from '../activity-move-drop-zone-v2/activity-move-drop-zone-v2.component';
import { ActivityShiftDropZoneV2Component } from '../activity-shift-drop-zone-v2/activity-shift-drop-zone-v2.component';
import { ActivitySnapLineV2Component } from '../activity-snap-line-v2/activity-snap-line-v2.component';
import { AddEmptyActivityToShiftDropZoneComponent } from '../add-empty-activity-to-shift-drop-zone/add-empty-activity-to-shift-drop-zone.component';
import { AssignShiftDropZoneV2Component } from '../assign-shift-drop-zone-v2/assign-shift-drop-zone-v2.component';
import { NewShiftDropZoneV2Component } from '../new-shift-drop-zone-component-v2/new-shift-drop-zone-component-v2.component';
import { NewShiftFromEmptyActivityDropZoneComponent } from '../new-shift-from-empty-activity-drop-zone/new-shift-from-empty-activity-drop-zone.component';
import { ShiftTemplateDropZoneV2Component } from '../shift-template-drop-zone-v2/shift-template-drop-zone-v2.component';
import { TimelineAutoSizeUserShiftsV2Component } from '../timeline-auto-size-user-shifts-v2/timeline-auto-size-user-shifts-v2.component';

import {
  wilsonCancelIdleCallback,
  wilsonRequestIdleCallback,
} from '@wilson/utils';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RxFor } from '@rx-angular/template/for';
import { GetAbsenceTimelineCardTitlePipe } from '@wilson/absences';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import { determineClusterFromClickPosition } from '@wilson/non-domain-specific/overlap-helpers';
import { StaysDrawerStateService } from '@wilson/non-domain-specific/stays-form-and-drawer';
import {
  NzContextMenuService,
  NzDropdownMenuComponent,
  NzDropDownModule,
} from 'ng-zorro-antd/dropdown';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { MoveAbsenceDropZoneHelperService } from '../../components/move-absence-drop-zone/move-absence-drop-zone-helper.service';
import { TimelineRowDragHandlerV2Directive } from '../../components/timeline-directives/timeline-row-drag-handler-v2.directive';
import { TimelineUserStaysComponent } from '../../components/timeline-user-stays/timeline-user-stays.component';
import { cloneAndSortShiftViewsByStartDatetime } from '../../services/utils/clone-and-sort-shifts';
import { ClusterDragHelperService } from './cluster-drag-helper.service';
import { TimelineInnerRowDynamicHeightPipe } from './timeline-inner-row-height.pipe';
import { TimelineUserRowContextMenuWrapperComponent } from './timeline-user-row-context-menu-wrapper/timeline-user-row-context-menu-wrapper.component';
import { TimelineUserRowContextMenuWrapperService } from './timeline-user-row-context-menu-wrapper/timeline-user-row-context-menu-wrapper.service';
import { TimelineShiftHeightPipe } from './timeline-user-row-shift-height.pipe';

@Component({
  selector: 'wilson-timeline-user-row-shifts-v2',
  templateUrl: './timeline-user-row-shifts-v2.component.html',
  styleUrls: ['./timeline-user-row-shifts-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ShiftTransferHandlerV2Service,
    ClusterDragHelperService,
    MoveAbsenceDropZoneHelperService,
    ShiftCardTooltipStateService,
  ],
  imports: [
    TimelineUserRowContextMenuWrapperComponent,
    CommonModule,
    NewAbsenceDropZoneComponent,
    AssignShiftDropZoneV2Component,
    ActivityDropZoneV2Component,
    NewShiftFromEmptyActivityDropZoneComponent,
    ActivityResetDropZoneComponent,
    ActivityMoveDropZoneV2Component,
    ActivitySnapLineV2Component,
    ActivityGapFillAnimationComponent,
    ActivityShiftDropZoneV2Component,
    AddEmptyActivityToShiftDropZoneComponent,
    ShiftTimelinePipesModule,
    TimelineUserAbsenceComponent,
    ToAbsenceDataPipe,
    TimelineAutoSizeUserShiftsV2Component,
    GanttChartTimeDifferenceComponent,
    ToDateTimeRangeV2Pipe,
    NewShiftDropZoneV2Component,
    ShiftTemplateDropZoneV2Component,
    TimelineUserStaysComponent,
    NzDropDownModule,
    RxFor,
    GetAbsenceTimelineCardTitlePipe,
    TranslateModule,
    NzSkeletonModule,
    NzMessageTimerLineComponent,
    MessageWithUndoOptionComponent,
    TimelineShiftHeightPipe,
    ShiftValidationTooltipWrapperComponent,
    DetermineShiftRenderDatetimePipe,
    TimeLineAbsenceTooltipComponent,
    NzTooltipModule,
    FeatureFlagsModule,
    TimelineRowDragHandlerV2Directive,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    TimeDifferenceWidthPxPipe,
    TimelineInnerRowDynamicHeightPipe,
  ],
})
export class TimelineUserRowShiftsV2Component implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  readonly mouseMovementHelperService = inject(MouseMovementHelperService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly nzContextMenuService = inject(NzContextMenuService);
  private readonly shiftTransferHandlerV2Service = inject(
    ShiftTransferHandlerV2Service,
  );
  private readonly changedKeyManagerService = inject(ChangedKeyManagerService);
  private readonly updateShiftOnActivityDragService = inject(
    UpdateShiftOnActivityDragService,
  );
  private readonly timelineUserRowContextMenuWrapperService = inject(
    TimelineUserRowContextMenuWrapperService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  protected readonly staysDrawerStateService = inject(StaysDrawerStateService);
  protected readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  protected readonly deferredClusterCardHelperService = inject(
    DeferredClusterCardHelperService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  protected readonly shiftCardTooltipStateService = inject(
    ShiftCardTooltipStateService,
  );
  private readonly assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  protected readonly clusterDragHelperService = inject(
    ClusterDragHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  private rowId = Math.random();
  private idleCallbackId: ReturnType<typeof wilsonRequestIdleCallback> | null =
    null;

  @Input() user!: UserTimelines;
  @Input() userIndex!: number;
  @Input() set userDataRowsData(value: GroupedUserDataV2[]) {
    const previousValue = this.userDataRowsSubject.value;
    if (
      (previousValue.length === 0 && value.length > 0) ||
      (previousValue.length > 0 && value.length === 0)
    ) {
      // We don't ever really need to reload the row unless the user scrolls back into a row that has data again or vice versa.
      // This happens when user opens and close the unassigned region
      this.rowId = Math.random();
    }
    this.userDataRowsSubject.next(value);
  }
  @Input() mouseOverRowIndex!: number | null;
  @Input() defaultRowHeight!: number | null;

  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;
  protected INNER_ROW_PADDING_TOP_PX = INNER_ROW_PADDING_TOP_PX;
  protected INNER_ROW_PADDING_BOTTOM_PX = INNER_ROW_PADDING_BOTTOM_PX;
  protected ROW_PADDING = ROW_PADDING;
  protected CLUSTER_CARD_HEIGHT_ADJUSTMENT = 2;
  protected STAY_CARD_DEFAULT_HEIGHT = 18;

  MOCK_SHIFT_ID = CreateNewViaDragAndDropService.MOCK_SHIFT_ID;

  subscriptions = new Subscription();
  dropZoneShiftView!: AccurateDateIntervalWithShift | null | undefined;
  staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  StaffLayoutStyle = StaffLayoutStyle;
  protected userDataRowsSubject = new BehaviorSubject<GroupedUserDataV2[]>([]);
  protected cluster!: {
    shifts: AccurateDateIntervalWithShift[];
    absences: AccurateDateIntervalWithAbsence[];
    stays: AccurateDateIntervalWithStay[];
  };

  protected can = inject(AbilityServiceSignal).can;
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.dragAndDropModeService.setTimeDifferenceVisible(true);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.dragAndDropModeService.setTimeDifferenceVisible(false);
  }

  trackByUserRows: TrackByFunction<GroupedUserDataV2> = (
    _: number,
    groupedUserData: GroupedUserDataV2,
  ) => {
    return this.rowId;
  };

  trackByShiftViewDetails: TrackByFunction<AccurateDateIntervalWithShift> = (
    _: number,
    shiftView: AccurateDateIntervalWithShift,
  ) => {
    return this.changedKeyManagerService.computeChangedKeyForShiftViewModelV2(
      shiftView,
    );
  };

  ngOnInit(): void {
    this.dragAndDropModeService.setTimeDifferenceVisible(false);
    this.subscriptions.add(
      combineLatest([
        this.userDataRowsSubject.pipe(debounceTime(10)),
        this.activityDropZoneHelperService.activity$,
      ]).subscribe(([userDataRows, activity]) => {
        if (activity && userDataRows.length > 0) {
          this.dropZoneShiftView = this.getShiftView(
            activity.shiftId,
            userDataRows,
          );
        } else {
          this.dropZoneShiftView = null;
        }
      }),
    );
    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;

    this.subscriptions.add(
      this.userDataRowsSubject
        .pipe(debounceTime(10), pairwise())
        .subscribe(([previousUserDataRows, currentUserDataRows]) => {
          if (this.idleCallbackId) {
            wilsonCancelIdleCallback(this.idleCallbackId);
          }
          this.idleCallbackId = wilsonRequestIdleCallback(() => {
            this.assignedRegionVisibilityStateService.updateRegularTimelineShiftsVisibilityForUser(
              currentUserDataRows,
              previousUserDataRows,
              this.user.id,
            );
          });
        }),
    );
  }

  getShiftView(
    id: string | null,
    userDataRows: GroupedUserDataV2[],
  ): AccurateDateIntervalWithShift | null {
    for (const innerRowItem of userDataRows) {
      for (const shiftView of innerRowItem.shifts) {
        if (shiftView.shift.id === id) {
          return shiftView;
        }
      }
    }
    return null;
  }

  openAbsenceDrawer(absencePosition: AbsenceCardPosition): void {
    this.timelineCommonOperationsService.openAbsenceDrawer(
      absencePosition.left,
      absencePosition.right,
      absencePosition.id,
    );
  }

  editStayDrawer(stayPosition: StayCardPosition): void {
    this.staysDrawerStateService.editStay(stayPosition);
    this.store.dispatch([
      new SetDemarcatorRange([
        {
          left: stayPosition.left,
          right: stayPosition.right,
        },
      ]),
    ]);
  }

  async openActivityDrawer(
    event: SelectedItemOutput,
    userId: string | null,
  ): Promise<void> {
    if (userId) {
      this.subscriptions.add(
        this.timelineCommonOperationsService
          .openActivityDrawer(event)
          .subscribe(async (action) => {
            if (action?.type === ActivityDrawerCloseActionType.UPDATE) {
              if (action.activity.shiftId) {
                this.timelineCommonOperationsService.updateUsersAssignmentsState(
                  [userId],
                );
                this.store.dispatch(
                  new TimelineUpsertActivityV2(
                    [action.activity],
                    action.activity.shiftId,
                  ),
                );
              }
            } else if (
              action?.type === ActivityDrawerCloseActionType.DELETE_SHIFT
            ) {
              this.store.dispatch(
                new TimelineRemoveShiftFromUserV2({
                  shiftId: action.shiftId,
                  userId,
                }),
              );
            } else if (
              action?.type ===
              ActivityDrawerCloseActionType.DISSOCIATE_FROM_SHIFT
            ) {
              this.timelineCommonOperationsService.updateUsersAssignmentsState([
                userId,
              ]);
              this.store.dispatch(
                new TimelineRemoveActivityFromShiftV2(
                  action.activity.id as string,
                  action.activity.shiftId as string,
                ),
              );
              this.store.dispatch(
                new AddActivityToUnassignedRegion(action.activity),
              );
            } else if (
              action?.type === ActivityDrawerCloseActionType.DELETE &&
              action.activity.shiftId //This check is important because we will implement drawer for unassigned activity as well
            ) {
              await this.updateShiftOnActivityDragService.updateOrDeleteShiftOnTimeline(
                action.activity.id as string,
                action.activity.shiftId,
                userId,
              );
              this.store.dispatch(
                new TimelineRemoveActivityFromShiftV2(
                  action.activity.id as string,
                  action.activity.shiftId,
                ),
              );
            }

            this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
              [this.user.id],
            );
          }),
      );
    }
  }

  openStaysDrawer({
    shiftViews,
    index,
    timelineCards,
  }: {
    shiftViews: AccurateDateIntervalWithShift[];
    index: number;
    timelineCards: (LocationAndDateTimeRange & { shiftId?: string })[];
  }): void {
    const clonedSortedShiftViews =
      cloneAndSortShiftViewsByStartDatetime(shiftViews);
    const currentItem = timelineCards[index];
    const previousItem = timelineCards[index - 1];

    const previousShift = clonedSortedShiftViews.find(
      (shiftView) => shiftView.shift.id === previousItem.shiftId,
    )?.shift;
    if (previousShift) {
      this.staysDrawerStateService.setStaysDrawerRelatedAdjacentShifts({
        nextTimelineCard: currentItem,
        previousShift: previousShift,
      });
      this.staysDrawerStateService.toggleCreateStaysDrawer(true);
    } else {
      this.nzMessageService.error(
        this.translateService.instant('general.error.previousShiftNotFound'),
      );
    }
  }

  openStayDrawerWithSingleShift(
    shiftViews: AccurateDateIntervalWithShift[],
    index: number,
  ): void {
    const clonedSortedShiftViews =
      cloneAndSortShiftViewsByStartDatetime(shiftViews);

    const currentShift = clonedSortedShiftViews[index].shift;

    const staySuggestionInfo: StaySuggestionInfo = {
      locationId: currentShift.endLocationId,
      userId: currentShift.userId as string,
      shiftId: currentShift.id,
      shift: currentShift,
      date: currentShift.endDatetime,
      locationName: `${currentShift.endLocation.locationCode}${
        currentShift.endLocation.locationCode ? ' - ' : ''
      }${currentShift.endLocation.name}`,
      userName: `${this.user.lastName}, ${this.user.firstName}`,
      cancellationDate: null,
    };
    this.staysDrawerStateService.setStaySuggestionInfo(staySuggestionInfo);
    this.staysDrawerStateService.toggleCreateStaysDrawer(true);
  }

  openTransferModal(
    shiftViews: AccurateDateIntervalWithShift[],
    timelineCards: (LocationAndDateTimeRange & { shiftId?: string })[],
    index: number,
  ): void {
    this.shiftTransferHandlerV2Service.openTransferModal({
      shiftViews,
      timelineCards,
      index,
      userId: this.user.id,
      organizationalUnitId: this.user.organizationalUnitId,
    });
  }

  contextMenu(
    event: MouseEvent,
    menu: NzDropdownMenuComponent,
    rowContainer: HTMLDivElement,
  ): void {
    if (this.user.id && event.target === rowContainer) {
      this.timelineUserRowContextMenuWrapperService.createDropDown({
        menuService: this.nzContextMenuService,
        mouseEvent: event,
        menu,
        userId: this.user.id,
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    wilsonRequestIdleCallback(() => {
      this.assignedRegionVisibilityStateService.clearAllShiftVisibilityRecordForUser(
        this.user.id,
      );
    });
  }

  async getClusterForSelectedItem(
    event: MouseEvent,
    leftPx: number,
  ): Promise<void> {
    const timeframe = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    );
    const frameLength = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );
    const pixelPosition = leftPx + event.layerX;

    const clickPositionDatetime = getDateFromPixelPosition({
      pixelPosition,
      timeframe,
      frameLength: frameLength,
    });

    const userDataRows = await firstValueFrom(this.userDataRowsSubject);
    this.cluster = determineClusterFromClickPosition(
      clickPositionDatetime,
      userDataRows[userDataRows.length - 1],
    );
    this.cluster.shifts.forEach((shiftView) => {
      this.assignedRegionVisibilityStateService.updateShiftVisibility(
        true,
        shiftView.shift.id,
        this.user.id,
      );
    });
  }

  async checkShiftKeyOnClick(
    event: MouseEvent,
    shiftId: string,
    undoTemplate: TemplateRef<void>,
  ): Promise<void> {
    await this.shiftMultiSelectHelperService.handleClick({
      shiftId,
      event,
      leftPixelPosition: null,
      rightPixelPosition: null,
      singleShiftSelectedCallback: () => {
        // This function is intentionally left empty
      },
      templateRef: undoTemplate,
      messageDuration: this.MESSAGE_DURATION,
      isUnAssignedShift: false,
    });
  }
}
